<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'infringementsList' }">{{ t('back') }}</ButtonBack>
    <AuditLogButton
      v-if="infringement"
      :entity-type="AuditLogEntityType.INFRINGEMENT"
      :entity-id="infringement.id"
    />
  </ButtonBackContainer>

  <section class="carvia-container">
    <div v-if="infringement" class="space-y-6">
      <div class="flex flex-wrap items-center gap-2">
        <InfringementStatusBadge :status="infringement.status" size="md" />
        <CircleXIcon
          v-if="infringement.status !== InfringementStatus.COMPLETED && hasErrorInCurrentStatus"
          class="size-4 text-bittersweet-600"
        />
        <Spinner v-else-if="showLoadingSpinner" class="size-4 animate-spin text-primary" />
      </div>

      <div
        v-if="errorSteps.length && infringement.status !== InfringementStatus.COMPLETED"
        class="space-y-2"
      >
        <StatusBanner v-for="step in errorSteps" :key="step" variant="problem">
          <span>{{ t(`infringementSteps.${step}`) }}</span>
          <template v-if="!hasDuplicateFileNumberError && isRetryableStep(step)" #actions>
            <CVButton
              variant="link"
              size="sm"
              :is-loading="
                (isInitiatingRetryStep && retryStepInitiating === step) || isRetrying(step)
              "
              :disabled="showLoadingSpinner || !canRetryStep(step)"
              @click="retryStep(step)"
            >
              {{ t('retry') }}
            </CVButton>
          </template>
        </StatusBanner>
        <template v-if="hasDuplicateFileNumberError">
          <StatusBanner variant="warning">
            {{ t('duplicateFileNumberCannotExecute') }}
            <template #actions>
              <CVButton
                variant="link"
                size="sm"
                :to="{ name: 'infringementDetail', params: { id: duplicateInfringement?.id } }"
              >
                {{ t('viewDuplicateOriginal') }}
              </CVButton>
            </template>
          </StatusBanner>
        </template>
      </div>

      <StatusBanner v-if="infringement.waivedReason" variant="success">
        <span class="font-medium">{{ t('companyAbsorption') }}</span>
        <span>{{ t('waiveReason') }}: {{ t(`waiveReasons.${infringement.waivedReason}`) }}</span>
        <span v-if="infringement.waivedReasonComment"
          >– {{ infringement.waivedReasonComment }}</span
        >
      </StatusBanner>

      <div
        v-if="infringement.status === InfringementStatus.AWAITING_USER_CONFIRMATION"
        class="space-y-4"
      >
        <h2>{{ t('checkAndExecute') }}</h2>
        <p>{{ t('awaitingUserConfirmationDescription') }}</p>
        <LabeledText
          v-if="requiresChargeFeeVatSelection"
          :label="t('chargeFeeVatMode')"
          :info-text="t('chargeFeeVatModeInfo')"
        >
          <Select
            v-model="chargeFeeVatChoice"
            class="max-w-md"
            :options="chargeFeeVatSelectOptions"
            empty-value-to="null"
            :disabled="isManualUpdating"
            variant="inline"
          />
        </LabeledText>
        <CVButton
          v-if="!hasDuplicateFileNumberError"
          :is-loading="isExecutingInfringement"
          :disabled="!canConfirmExecution || isManualUpdating"
          @click="confirmInfringement"
        >
          {{ t('confirmInfringementAndExecute') }}
        </CVButton>
      </div>

      <div v-if="infringement.data" class="space-y-4">
        <div class="flex items-center justify-between">
          <h2>{{ t('infringementDetails') }}</h2>
          <CVButton
            v-if="isManualEditEnabled && !isEditingData"
            variant="ghost"
            size="iconSm"
            @click="isEditingData = true"
          >
            <PencilIcon class="size-4" />
          </CVButton>
        </div>
        <InfringementManualEditForm
          v-if="isEditingData"
          :infringement="infringement"
          mode="data"
          :is-loading="isManualUpdating"
          @cancel="isEditingData = false"
          @submit="saveInfringementData"
        />
        <div v-else class="grid gap-4 md:grid-cols-2">
          <LabeledText :label="t('date')">
            {{ formatDate(infringement.data.date, undefined, '-') }}
          </LabeledText>

          <LabeledText :label="t('licencePlate')">
            <LicencePlate>{{ infringement.data.licencePlate || '-' }}</LicencePlate>
          </LabeledText>

          <LabeledText :label="t('documentLanguage')">
            {{ formateLanguage(infringement.data.documentLanguage) }}
          </LabeledText>

          <LabeledText :label="t('country')" :info-text="t('countryInfoText')">
            {{ getCountryWithFeeLabel(infringement.data.country) }}
          </LabeledText>

          <LabeledText :label="t('authority')">
            {{ infringement.data.authority || '-' }}
          </LabeledText>

          <LabeledText :label="t('email')">
            {{ infringement.data.email || '-' }}
          </LabeledText>

          <LabeledText :label="t('fileNumber')">
            {{ infringement.data.fileNumber || '-' }}
          </LabeledText>

          <LabeledText :label="t('infringementType')">
            {{
              infringement.data.infringementType
                ? t(`infringementTypes.${infringement.data.infringementType}`)
                : '-'
            }}
          </LabeledText>

          <LabeledText
            :label="t('skipSendAuthorityMail')"
            :info-text="t('skipSendAuthorityMailInfo')"
          >
            <Select
              v-if="isManualEditEnabled"
              v-model="skipAuthorityMailChoice"
              class="max-w-md"
              :options="skipAuthorityMailSelectOptions"
              :disabled="isManualUpdating"
              variant="inline"
            />
            <span v-else>{{
              infringement.skipSendAuthorityMail
                ? t('authorityMailChoiceSkip')
                : t('authorityMailChoiceSend')
            }}</span>
          </LabeledText>
        </div>
      </div>

      <div
        v-if="
          (infringement.rosBooking ||
            infringement.wunderBooking ||
            infringement.steps?.extractData === 'error' ||
            infringement.steps?.findBooking === 'error') &&
          !hasDuplicateFileNumberError
        "
        class="space-y-4"
      >
        <div class="flex items-center justify-between">
          <h2>{{ t('foundBookingAndDriverData') }}</h2>
          <div
            v-if="isManualEditEnabled && (infringement.rosBooking || infringement.wunderBooking)"
            class="flex items-center gap-2"
          >
            <CVButton
              variant="secondary"
              size="sm"
              :is-loading="
                (isInitiatingRetryStep && retryStepInitiating === 'findBooking') ||
                isRetrying('findBooking')
              "
              :disabled="showLoadingSpinner || !canRetryFindBooking"
              @click="retryStep('findBooking')"
            >
              <RefreshCwIcon class="size-4" /> {{ t('retry') }}
            </CVButton>
            <CVButton
              v-if="!isEditingBooking"
              variant="ghost"
              size="iconSm"
              @click="isEditingBooking = true"
            >
              <PencilIcon class="size-4" />
            </CVButton>
          </div>
        </div>

        <InfringementManualEditForm
          v-if="isEditingBooking"
          :infringement="infringement"
          mode="booking"
          :is-loading="isManualUpdating"
          @cancel="isEditingBooking = false"
          @submit="saveBooking"
        />

        <div
          v-else-if="infringement.wunderBooking || infringement.rosBooking"
          class="grid gap-4 md:grid-cols-2"
        >
          <LabeledText v-if="infringement.rosBooking" :label="t('rosBookingNumber')">
            <router-link
              :to="{ name: 'bookingEdit', params: { id: infringement.rosBooking.id } }"
              class="underline"
            >
              {{ infringement.rosBooking.bookingNumber }}
            </router-link>
          </LabeledText>

          <LabeledText v-if="infringement.wunderBooking" :label="t('wunderRentalId')">
            {{ infringement.wunderBooking?.id }}
          </LabeledText>

          <LabeledText :label="t('bookingPeriod')">
            <template v-if="infringement.wunderBooking">
              {{ getBookingPeriod() }}
            </template>
            <template v-else>
              <div>{{ t('actual') }}: {{ getRosHandoverAndReturnDates() }}</div>
              <div>{{ t('booked') }}: {{ getBookingPeriod() }}</div>
            </template>
          </LabeledText>

          <LabeledText :label="t('driverPhone')">
            {{ getDriverPhone() }}
          </LabeledText>

          <LabeledText :label="t('driverEmail')">
            {{ getDriverEmail() }}
          </LabeledText>

          <LabeledText :label="t('driverData')">
            <div class="whitespace-pre-line">
              {{ getDriverData() }}
            </div>
          </LabeledText>
        </div>

        <div v-else class="rounded-lg bg-white p-4">
          <div class="flex items-center gap-2">
            <p>{{ t('noBookingFoundHint') }}</p>
            <CVButton
              variant="secondary"
              size="sm"
              :is-loading="
                (isInitiatingRetryStep && retryStepInitiating === 'findBooking') ||
                isRetrying('findBooking')
              "
              :disabled="showLoadingSpinner || !canRetryFindBooking"
              @click="retryStep('findBooking')"
            >
              <RefreshCwIcon class="size-4" /> {{ t('retry') }}
            </CVButton>
            <p>{{ t('or') }}</p>
            <CVButton v-if="isManualEditEnabled" size="sm" @click="isEditingBooking = true">
              {{ t('setBookingManually') }}
            </CVButton>
          </div>
        </div>

        <div v-if="canWaive" class="flex flex-col items-start gap-2 rounded-lg bg-white p-4">
          <p class="whitespace-pre-line">{{ t('waiveHint') }}</p>
          <CVButton
            variant="secondary"
            size="sm"
            :is-loading="isManualUpdating"
            @click="handleWaive"
          >
            {{ t('waiveButton') }}
          </CVButton>
        </div>

        <div v-if="postFindBookingSuccessStepEntries.length" class="space-y-4">
          <h2>{{ t('processing') }}</h2>
          <div class="space-y-2">
            <StatusBanner
              v-for="[step] in postFindBookingSuccessStepEntries"
              :key="step"
              variant="success"
            >
              <span>{{ t(`infringementSteps.${step}`) }}</span>
            </StatusBanner>
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <h2>{{ t('comment') }}</h2>
          <CVButton
            v-if="!isEditingComment"
            variant="ghost"
            size="iconSm"
            @click="
              () => {
                isEditingComment = true;
                comment = infringement?.comment ?? '';
              }
            "
          >
            <PencilIcon class="size-4" />
          </CVButton>
        </div>
        <div v-if="isEditingComment" class="space-y-4">
          <Textarea
            v-model:input-text="comment"
            :placeholder="t('commentPlaceholder')"
            :disabled="isManualUpdating"
            :initial-rows="4"
          />
          <div class="flex justify-end gap-4">
            <CVButton
              variant="default"
              :is-loading="isManualUpdating"
              :disabled="isManualUpdating"
              size="sm"
              @click="saveComment"
            >
              {{ t('save') }}
            </CVButton>
            <CVButton
              variant="secondary"
              size="sm"
              :disabled="isManualUpdating"
              @click="isEditingComment = false"
            >
              {{ t('cancel') }}
            </CVButton>
          </div>
        </div>
        <p v-else class="whitespace-pre-line">{{ infringement.comment || '-' }}</p>
      </div>

      <div class="space-y-4">
        <h2>{{ t('scan') }}</h2>
        <CVButton v-if="isScanPdf" variant="secondary" @click="openScanPdfPreview">
          {{ t('openScanPdfPreview') }}
        </CVButton>
        <img v-else :src="infringement.imageFileUrl" :alt="t('infringementImage')" />
      </div>

      <CVButton
        v-if="isDeletable"
        variant="danger"
        size="sm"
        :is-loading="isDeletingInfringement"
        @click="handleDeleteInfringement"
      >
        {{ t('deleteInfringement') }}
      </CVButton>
    </div>

    <div v-else class="flex justify-center py-12">
      <Spinner />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, watch, watchEffect } from 'vue';
import type { SelectOption } from '@/components/Select.vue';
import Select from '@/components/Select.vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import LabeledText from '@/components/LabeledText.vue';
import Spinner from '@/components/icons/Spinner.vue';
import {
  INFRINGEMENTS_QUERY_KEY,
  type RetryableInfringementStep,
  useDeleteInfringement,
  useExecuteInfringement,
  useInfringement,
  useInfringements,
  useRetryInfringementStep,
  useUpdateInfringement,
} from '@/queries/use-infringements';
import { formatDate } from '@/hooks/use-formatted-date';
import { useUiStore } from '@/stores/ui.store';
import InfringementStatusBadge from '@/components/badges/InfringementStatusBadge.vue';
import LicencePlate from '@/components/LicencePlate.vue';
import {
  InfringementChargeFeeVatMode,
  InfringementStatus,
  InfringementWaivedReason,
  type UpdateInfringementDto,
} from '@carvia/ros-client-types';

import CVButton from '@/components/buttons/CVButton.vue';
import { CircleXIcon, PencilIcon, RefreshCwIcon } from 'lucide-vue-next';
import { useInfringementStatusEvents } from '@/queries/use-infringement-status-events';
import { updateItemOnQueriesData } from '@/utils/update-query-data';
import { useQueryClient } from '@tanstack/vue-query';
import { useInfringementStepState } from '@/hooks/use-infringement-step-state';
import InfringementManualEditForm from './InfringementManualEditForm.vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { Alert } from '@/services/alert.service';
import router from '@/router';
import Textarea from '@/components/Textarea.vue';
import AuditLogButton from '@/components/audit-log/AuditLogButton.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { fetchPdfBlobFromUrl } from '@/utils/download-file';
import { AuditLogEntityType } from '@carvia/ros-client-types';
import { getCountries } from '@/utils/countries';
import { useYieldManagementSettings } from '@/queries/use-yield-management-settings';
import { formatCurrency } from '@/utils/format-numbers';
import { compareLicencePlates } from '@/utils/compare-licence-plates';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { isNonStandardTaxProcedure } from '@/utils/tax-procedure';

const { t, locale } = useI18n();
const uiStore = useUiStore();
const queryClient = useQueryClient();
const route = useRoute();

watchEffect(() => {
  uiStore.setHeaderTitle(t('infringement'));
});

const infringementId = computed(() => route.params.id as string);
const { data: infringement } = useInfringement(infringementId);
const { data: yieldManagementSettings } = useYieldManagementSettings();
const { mutateAsync: executeInfringement, isPending: isExecutingInfringement } =
  useExecuteInfringement();
const { mutateAsync: retryInfringementStep, isPending: isInitiatingRetryStep } =
  useRetryInfringementStep();
const { mutateAsync: updateInfringement, isPending: isManualUpdating } = useUpdateInfringement();
const { mutateAsync: deleteInfringement, isPending: isDeletingInfringement } =
  useDeleteInfringement();
const retryStepInitiating = ref<RetryableInfringementStep | null>(null);
const inProgressRetrySteps = ref<Partial<Record<RetryableInfringementStep, true>>>({});
const isEditingData = ref(false);
const isEditingBooking = ref(false);
const isEditingComment = ref(false);
const comment = ref('');
const pdfPreview = usePdfPreview();

watch(infringementId, () => {
  isEditingData.value = false;
  isEditingBooking.value = false;
  isEditingComment.value = false;
  inProgressRetrySteps.value = {};
});

const {
  stepEntries: infringementStepEntries,
  errorSteps,
  hasErrorInCurrentStatus,
  showLoadingSpinner,
} = useInfringementStepState(infringement);

const postFindBookingSuccessStepEntries = computed(() =>
  infringementStepEntries.value.filter(
    ([step, status]) =>
      status === 'success' &&
      (step === 'chargeFee' ||
        step === 'sendPaymentLink' ||
        step === 'sendInvoice' ||
        step === 'sendAuthorityEmail'),
  ),
);

const hasDuplicateFileNumberError = computed(() => {
  const steps = infringement.value?.steps as Record<string, string | undefined> | undefined;
  return steps?.checkDuplicateFileNumber === 'error';
});

const { data: duplicateInfringementsData } = useInfringements(
  computed(() => ({
    limit: 50,
    search: infringement.value?.data?.fileNumber ?? undefined,
  })),
  hasDuplicateFileNumberError,
);
const duplicateInfringements = useFlattenPaginatedData(duplicateInfringementsData);

const duplicateInfringement = computed(() =>
  duplicateInfringements.value.find(
    (infringement) => infringement.steps?.checkDuplicateFileNumber === 'success',
  ),
);

const isRetryableStep = (step: string): step is RetryableInfringementStep =>
  step === 'findBooking' ||
  (step === 'chargeFee' && !infringement.value?.steps?.sendPaymentLink) ||
  step === 'sendPaymentLink' ||
  step === 'sendInvoice' ||
  step === 'sendAuthorityEmail';

const canRetryFindBooking = computed(
  () =>
    infringement.value?.data?.date &&
    infringement.value?.data?.infringementType &&
    infringement.value?.data?.licencePlate,
);
const canRetryStep = (step: RetryableInfringementStep) =>
  step === 'findBooking' ? canRetryFindBooking.value : true;
const isRetrying = (step: RetryableInfringementStep): boolean => !!inProgressRetrySteps.value[step];

const isDeletable = computed(() => {
  return [
    InfringementStatus.PENDING,
    InfringementStatus.EXTRACTING_DATA,
    InfringementStatus.AWAITING_USER_CONFIRMATION,
  ].includes(infringement.value?.status ?? InfringementStatus.PENDING);
});

const isManualEditEnabled = computed(
  () =>
    infringement.value?.status === InfringementStatus.EXTRACTING_DATA ||
    infringement.value?.status === InfringementStatus.AWAITING_USER_CONFIRMATION,
);

type AuthorityMailChoice = 'send' | 'skip';

const skipAuthorityMailChoice = ref<AuthorityMailChoice>('send');

watchEffect(() => {
  skipAuthorityMailChoice.value = infringement.value?.skipSendAuthorityMail ? 'skip' : 'send';
});

const skipAuthorityMailSelectOptions = computed((): SelectOption[] => [
  { label: t('authorityMailChoiceSend'), value: 'send' },
  { label: t('authorityMailChoiceSkip'), value: 'skip' },
]);

watch(skipAuthorityMailChoice, async (choice) => {
  if (!infringement.value || !isManualEditEnabled.value) return;
  const skip = choice === 'skip';
  if (skip === infringement.value.skipSendAuthorityMail) return;
  try {
    await updateInfringement({
      id: infringementId.value,
      payload: { skipSendAuthorityMail: skip },
    });
  } catch {
    skipAuthorityMailChoice.value = infringement.value.skipSendAuthorityMail ? 'skip' : 'send';
  }
});

type ChargeFeeVatChoice = InfringementChargeFeeVatMode | undefined;

const chargeFeeVatChoice = ref<ChargeFeeVatChoice | null>(null);

watchEffect(() => {
  chargeFeeVatChoice.value = infringement.value?.chargeFeeVatMode ?? null;
});

const requiresChargeFeeVatSelection = computed(() => !!infringement.value?.rosBooking);

const bookingRequiresNetProcessingFee = computed(() => {
  const taxProcedure = infringement.value?.rosBooking?.taxProcedure;
  return !!taxProcedure && isNonStandardTaxProcedure(taxProcedure);
});

const chargeFeeVatSelectOptions = computed((): SelectOption[] => [
  ...(chargeFeeVatChoice.value ? [] : [{ label: t('chargeFeeVatModePlaceholder'), value: '' }]),
  ...(bookingRequiresNetProcessingFee.value
    ? []
    : [{ label: t('chargeFeeWithVat'), value: InfringementChargeFeeVatMode.WITH_VAT }]),
  { label: t('chargeFeeWithoutVat'), value: InfringementChargeFeeVatMode.WITHOUT_VAT },
]);

const canConfirmExecution = computed(() => {
  if (!requiresChargeFeeVatSelection.value) return true;
  if (!infringement.value?.chargeFeeVatMode) return false;
  return !(
    bookingRequiresNetProcessingFee.value &&
    infringement.value.chargeFeeVatMode === InfringementChargeFeeVatMode.WITH_VAT
  );
});

watch(chargeFeeVatChoice, async (choice) => {
  if (!infringement.value || !requiresChargeFeeVatSelection.value) {
    return;
  }
  if (choice === infringement.value.chargeFeeVatMode) return;
  try {
    await updateInfringement({
      id: infringementId.value,
      payload: { chargeFeeVatMode: choice },
    });
  } catch {
    chargeFeeVatChoice.value = infringement.value.chargeFeeVatMode ?? null;
  }
});

const canWaive = computed(
  () =>
    !hasDuplicateFileNumberError.value &&
    isManualEditEnabled.value &&
    infringement.value?.steps?.extractData,
);

const isScanPdf = computed(() => {
  return infringement.value?.imageFileKey.toLowerCase().endsWith('.pdf');
});

const rosBookingAllocation = computed(() => {
  return infringement.value?.rosBooking?.allocations.find((allocation) => {
    if (!infringement.value?.data?.date) {
      return false;
    }
    const a = allocation as unknown as Allocation;
    return (
      compareLicencePlates(
        a.car.licencePlate ?? '',
        infringement.value?.data?.licencePlate ?? '',
      ) &&
      (a.handoverDate ?? a.startDate) <= infringement.value.data.date &&
      (a.returnDate ?? a.endDate) >= infringement.value.data.date
    );
  });
});

const getBookingPeriod = () => {
  const booking =
    rosBookingAllocation.value ||
    infringement.value?.rosBooking ||
    infringement.value?.wunderBooking;
  return booking ? `${formatDate(booking.startDate)} - ${formatDate(booking.endDate)}` : '';
};

const getRosHandoverAndReturnDates = () => {
  const rosBooking = rosBookingAllocation.value || infringement.value?.rosBooking;
  return rosBooking
    ? `${formatDate(rosBooking.handoverDate)} - ${formatDate(rosBooking.returnDate)}`
    : '-';
};

const getDriverData = () => {
  const driverData =
    infringement.value?.rosBooking?.customerData || infringement.value?.wunderBooking?.customer;
  return driverData
    ? `${driverData.firstName} ${driverData.lastName}\n` +
        `${driverData.street}\n` +
        `${driverData.zip} ${driverData.city}\n` +
        `${driverData.country}`
    : '';
};

const getDriverPhone = () => {
  const driverData =
    infringement.value?.rosBooking?.customerData || infringement.value?.wunderBooking?.customer;
  return driverData?.phone ?? '';
};

const getDriverEmail = () => {
  const rosCustomerData = infringement.value?.rosBooking?.customer;
  if (rosCustomerData) {
    return rosCustomerData.email;
  }
  return infringement.value?.wunderBooking?.customer?.email ?? '';
};

const formateLanguage = (documentLanguage?: string | null) => {
  if (!documentLanguage) return '-';
  return documentLanguage.toUpperCase();
};

const countries = computed(() => getCountries(locale.value));
const getCountryName = (countryCode?: string | null) => {
  return countries.value.find((country) => country.value === countryCode)?.label ?? '-';
};

const getProcessingFeeAmount = (countryCode?: string | null) => {
  const fees = yieldManagementSettings.value?.infringementFees;
  if (!fees?.length) {
    return undefined;
  }
  const normalizedCountryCode = countryCode?.toUpperCase();
  const countrySpecificFee =
    normalizedCountryCode &&
    fees.find((entry) => (entry.countries ?? []).includes(normalizedCountryCode));
  if (countrySpecificFee) {
    return countrySpecificFee.amount;
  }
  const defaultFee = fees.find(
    (entry) => entry.countries === null || entry.countries === undefined,
  );
  return defaultFee?.amount;
};

const getProcessingFeeLabel = (countryCode?: string | null) => {
  const feeAmount = getProcessingFeeAmount(countryCode);
  return typeof feeAmount === 'number' ? formatCurrency(feeAmount) : '-';
};

const getCountryWithFeeLabel = (countryCode?: string | null) => {
  const countryName = getCountryName(countryCode);
  const feeLabel = getProcessingFeeLabel(countryCode);
  return feeLabel === '-' ? countryName : `${countryName} (${t('feeLabel')}: ${feeLabel})`;
};

const confirmInfringement = async () => {
  const { isConfirmed } = await Alert.fire({
    icon: 'warning',
    titleText: t('confirmExecuteTitle'),
    html: t('confirmExecuteDescription'),
    showCancelButton: true,
    confirmButtonText: t('confirmExecute'),
    cancelButtonText: t('cancel'),
  });
  if (!isConfirmed) {
    return;
  }

  await executeInfringement(infringementId.value);
};

const retryStep = async (step: RetryableInfringementStep) => {
  const { isConfirmed } = await Alert.fire({
    icon: 'warning',
    titleText: t('confirmRetryTitle'),
    html: t('confirmRetryDescription', { step: t(`infringementSteps.${step}`) }),
    showCancelButton: true,
    confirmButtonText: t('retry'),
    cancelButtonText: t('cancel'),
  });
  if (!isConfirmed) {
    return;
  }

  retryStepInitiating.value = step;
  try {
    await retryInfringementStep({ id: infringementId.value, step });
    inProgressRetrySteps.value = {
      ...inProgressRetrySteps.value,
      [step]: true,
    };
  } finally {
    retryStepInitiating.value = null;
  }
};

const saveInfringementData = async (payload: UpdateInfringementDto) => {
  await updateInfringement({
    id: infringementId.value,
    payload,
  });
  isEditingData.value = false;
};

const saveBooking = async (payload: UpdateInfringementDto) => {
  await updateInfringement({
    id: infringementId.value,
    payload,
  });
  isEditingBooking.value = false;
};

const saveComment = async () => {
  await updateInfringement({
    id: infringementId.value,
    payload: { comment: comment.value },
  });
  isEditingComment.value = false;
};

const handleWaive = async () => {
  const reasonOptions = Object.values(InfringementWaivedReason).map((reason) => ({
    label: t(`waiveReasons.${reason}`),
    value: reason,
  }));

  const { isConfirmed, value: reasonRaw } = await Alert.fire<string>({
    icon: 'warning',
    titleText: t('waiveModalTitle'),
    html: t('waiveModalDescription'),
    showCancelButton: true,
    confirmButtonText: t('waiveConfirm'),
    cancelButtonText: t('cancel'),
    input: 'select',
    inputOptions: reasonOptions,
    inputLabel: t('waiveReason'),
    inputValidator: (value?: string) => {
      if (!value) {
        return t('waiveReasonRequired');
      }
      return null;
    },
  });

  if (!isConfirmed || !reasonRaw) {
    return;
  }

  const reason = reasonRaw as InfringementWaivedReason;

  let waivedReasonComment: string | undefined;
  if (reason === InfringementWaivedReason.OTHER) {
    const { isConfirmed: isCommentConfirmed, value: comment } = await Alert.fire<string>({
      icon: 'warning',
      titleText: t('waiveReasonCommentTitle'),
      html: t('waiveCommentDescription'),
      input: 'textarea',
      inputLabel: t('waiveReasonComment'),
      inputPlaceholder: t('waiveReasonCommentPlaceholder'),
      showCancelButton: true,
      confirmButtonText: t('waiveConfirm'),
      cancelButtonText: t('cancel'),
      inputValidator: (value?: string) => {
        if (!value?.trim()) {
          return t('waiveReasonCommentRequired');
        }
        return null;
      },
    });
    if (!isCommentConfirmed || !comment?.trim()) {
      return;
    }
    waivedReasonComment = comment.trim();
  }

  await updateInfringement({
    id: infringementId.value,
    payload: {
      status: InfringementStatus.COMPLETED,
      waivedReason: reason,
      ...(reason === InfringementWaivedReason.OTHER ? { waivedReasonComment } : {}),
    },
  });
};

const handleDeleteInfringement = async () => {
  const { isDenied } = await Alert.fire({
    titleText: t('confirmDeleteInfringement'),
    icon: 'warning',
    showDenyButton: true,
    denyButtonText: t('delete'),
    showCancelButton: true,
    showConfirmButton: false,
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) return;
  await deleteInfringement(infringementId.value);
  router.replace({ name: 'infringementsList' });
};

const openScanPdfPreview = async () => {
  if (!infringement.value?.imageFileUrl) return;
  await pdfPreview.openPdfPreview({
    title: t('scan'),
    fileName: infringement.value.imageFileKey.split('/').at(-1),
    load: () => fetchPdfBlobFromUrl(infringement.value!.imageFileUrl!),
    errorMessage: t('couldNotLoadScanPdf'),
  });
};

const { events } = useInfringementStatusEvents();

watch(
  events,
  (newEvents) => {
    if (newEvents.length > 0) {
      const latestEvent = newEvents[newEvents.length - 1];
      if (latestEvent.type === 'update') {
        updateItemOnQueriesData([INFRINGEMENTS_QUERY_KEY], latestEvent.infringement, queryClient);
      }
      inProgressRetrySteps.value = {};
    }
  },
  { deep: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "infringement": "Infringement",
    "infringementDetails": "Infringement Details",
    "licencePlate": "Licence Plate",
    "documentLanguage": "Document Language",
    "country": "Country",
    "authority": "Authority",
    "email": "Email",
    "date": "Date",
    "fileNumber": "File Number",
    "feeLabel": "Fee",
    "infringementType": "Infringement Type",
    "comment": "Comment",
    "commentPlaceholder": "Add an internal comment",
    "checkAndExecute": "Check and Execute further actions",
    "awaitingUserConfirmationDescription": "Please confirm the infringement and execute the action. With the confirmation, the processing fee will be automatically charged, a bill will be created and the driver data will be forwarded to the respective authority.",
    "confirmInfringementAndExecute": "Confirm Infringement and Execute Actions",
    "confirmExecuteTitle": "Execute infringement?",
    "confirmExecuteDescription": "The processing fee will be charged, an invoice will be created, and driver data will be forwarded to the authority where applicable. This action cannot be undone.",
    "confirmExecute": "Execute",
    "confirmRetryTitle": "Retry step?",
    "confirmRetryDescription": "Do you want to retry \"{step}\"?",
    "duplicateFileNumberCannotExecute": "This infringement is flagged as a duplicate (same file number and email as another case). Execution is not allowed. Correct the data if this is a mistake.",
    "foundBookingAndDriverData": "Found Booking and Driver Data",
    "wunderRentalId": "Wunder Rental ID",
    "rosBookingNumber": "ROS Booking Number",
    "bookingPeriod": "Booking Period",
    "booked": "Booked",
    "actual": "Actual",
    "driverPhone": "Driver Phone",
    "driverEmail": "Driver Email",
    "driverData": "Driver Data",
    "scan": "Scan",
    "openScanPdfPreview": "Open PDF preview",
    "infringementImage": "Infringement scan",
    "couldNotLoadScanPdf": "The scan PDF could not be loaded. Please try again.",
    "retry": "Retry",
    "processing": "Processing",
    "noBookingFoundHint": "No booking was found yet. You can",
    "or": "or",
    "setBookingManually": "set the booking manually",
    "viewDuplicateOriginal": "View original",
    "deleteInfringement": "Delete Infringement",
    "confirmDeleteInfringement": "Do you really want to delete this infringement?",
    "countryInfoText": "This is used to determine the processing fee based on yield settings.",
    "waiveHint": "If it's our fault or similar, it may be that the infringement is on us. In this case please manually complete all necessary steps and",
    "waiveButton": "Set the infringement to completed",
    "waiveModalTitle": "Waive infringement",
    "waiveModalDescription": "The infringement will be marked completed. No fee will be charged and no driver data will be forwarded. Please select a reason.",
    "waiveCommentDescription": "Please describe the reason in text (required for \"Other\").",
    "waiveConfirm": "Confirm and complete",
    "waiveReasonRequired": "Please select a reason.",
    "waiveReasonCommentRequired": "A text reason is required.",
    "waiveReasonCommentTitle": "Details",
    "companyAbsorption": "Absorbed at company cost.",
    "waiveReason": "Reason",
    "waiveReasonComment": "Details",
    "waiveReasonCommentPlaceholder": "Describe the reason (required for \"Other\")",
    "waiveReasons": {
      "LATE_PARKING_VIOLATION": "Parking violation after 48 hours",
      "INTERNAL": "Internal ride",
      "OTHER": "Other"
    },
    "skipSendAuthorityMail": "Authority email",
    "skipSendAuthorityMailInfo": "If the driver data will be sent on other ways (e.g. because the authority does not accept emails), this step can be skipped",
    "authorityMailChoiceSend": "Send to authority",
    "authorityMailChoiceSkip": "Do not send",
    "chargeFeeVatMode": "Processing fee VAT",
    "chargeFeeVatModeInfo": "Choose whether the processing fee is charged including or excluding 19% VAT. This must be saved before execution. Reverse-charge and export bookings can only be charged without VAT.",
    "chargeFeeVatModePlaceholder": "Please select",
    "chargeFeeWithVat": "Calculate gross price (including 19% VAT)",
    "chargeFeeWithoutVat": "Calculate net price (excluding VAT)"
  },
  "de": {
    "infringement": "Ordnungswidrigkeit",
    "infringementDetails": "Details der Ordnungswidrigkeit",
    "licencePlate": "Kennzeichen",
    "documentLanguage": "Dokumentsprache",
    "country": "Land",
    "authority": "Behörde",
    "email": "E-Mail",
    "date": "Datum",
    "fileNumber": "Aktenzeichen",
    "feeLabel": "Gebühr",
    "infringementType": "Ordnungswidrigkeitstyp",
    "comment": "Kommentar",
    "commentPlaceholder": "Internen Kommentar hinzufügen",
    "checkAndExecute": "Überprüfen und weitere Aktionen ausführen",
    "awaitingUserConfirmationDescription": "Stell bitte sicher, dass die Daten mit der Ordnungswidrigkeit übereinstimmen. Mit der Bestätigung werden automatisch die Bearbeitungsgebühr eingezogen, eine Rechnung erstellt und die Fahrerdaten an die jeweilige Behörde weitergeleitet.",
    "confirmInfringementAndExecute": "Ordnungswidrigkeit bestätigen und Aktionen ausführen",
    "confirmExecuteTitle": "Ordnungswidrigkeit ausführen?",
    "confirmExecuteDescription": "Die Bearbeitungsgebühr wird eingezogen, eine Rechnung erstellt und – sofern vorgesehen – Fahrerdaten an die Behörde übermittelt. Diese Aktion kann nicht rückgängig gemacht werden.",
    "confirmExecute": "Ausführen",
    "confirmRetryTitle": "Schritt erneut ausführen?",
    "confirmRetryDescription": "Möchtest du „{step}“ erneut ausführen?",
    "duplicateFileNumberCannotExecute": "Diese Ordnungswidrigkeit ist als Duplikat markiert (gleiches Aktenzeichen und dieselbe E-Mail wie ein anderer Fall). Eine Ausführung ist nicht möglich. Bitte die Daten korrigieren, falls das ein Fehler ist.",
    "foundBookingAndDriverData": "Gefundene Buchung und Fahrerdaten",
    "wunderRentalId": "Wunder Miet-ID",
    "rosBookingNumber": "ROS Buchungsnummer",
    "bookingPeriod": "Zeitraum",
    "booked": "Gebucht",
    "actual": "Tatsächlich",
    "driverPhone": "Fahrer-Telefon",
    "driverEmail": "Fahrer-E-Mail",
    "driverData": "Fahrerdaten",
    "scan": "Scan",
    "openScanPdfPreview": "PDF-Vorschau öffnen",
    "infringementImage": "Scan der Ordnungswidrigkeit",
    "couldNotLoadScanPdf": "Das Scan-PDF konnte nicht geladen werden. Bitte versuche es erneut.",
    "retry": "Erneut versuchen",
    "processing": "Verarbeitung",
    "noBookingFoundHint": "Es wurde noch keine Buchung gefunden. Du kannst",
    "or": "oder",
    "setBookingManually": "Buchung manuell setzen",
    "viewDuplicateOriginal": "Original ansehen",
    "deleteInfringement": "Ordnungswidrigkeit löschen",
    "confirmDeleteInfringement": "Möchtest du diese Ordnungswidrigkeit wirklich löschen?",
    "countryInfoText": "Dies wird verwendet, um die Bearbeitungsgebühr anhand der Yield-Einstellungen zu bestimmen.",
    "waiveHint": "Wenn Eigenverschulden oder ähnliches vorliegt, kann es sein, dass die OWI auf unsern Nacken geht.\nIn diesem Fall, wickle bitte alles Nötige manuell ab und",
    "waiveButton": "Setze die Ordnungswidrigkeit auf abgeschlossen",
    "waiveModalTitle": "Ordnungswidrigkeit auf unsern Nacken abschließen",
    "waiveModalDescription": "Die Ordnungswidrigkeit wird auf abgeschlossen gesetzt. Es werden keine Gebühr eingezogen und keine Fahrerdaten weitergeleitet. Bitte einen Grund auswählen.",
    "waiveCommentDescription": "Bitte den Grund in Textform angeben (Pflicht bei „Sonstiges“).",
    "waiveConfirm": "Bestätigen und abschließen",
    "waiveReasonRequired": "Bitte einen Grund auswählen.",
    "waiveReasonCommentRequired": "Eine textliche Begründung ist erforderlich.",
    "waiveReasonCommentTitle": "Begründung",
    "companyAbsorption": "Auf Firmenkosten abgeschlossen.",
    "waiveReason": "Grund",
    "waiveReasonComment": "Begründung",
    "waiveReasonCommentPlaceholder": "Grund in Textform (Pflicht bei „Sonstiges“)",
    "waiveReasons": {
      "LATE_PARKING_VIOLATION": "Parkverstoß nach 48 Stunden",
      "INTERNAL": "Interne Fahrt",
      "OTHER": "Sonstiges"
    },
    "skipSendAuthorityMail": "Behörden-E-Mail",
    "skipSendAuthorityMailInfo": "Wenn die Fahrerdaten auf anderem Wege gesendet werden sollen (z.B. weil die Behörde keine E-Mail akzeptiert), kann der Schritt hier übersprungen werden",
    "authorityMailChoiceSend": "An Behörde senden",
    "authorityMailChoiceSkip": "Nicht senden",
    "chargeFeeVatMode": "Bearbeitungsgebühr MwSt.",
    "chargeFeeVatModeInfo": "Legt fest, ob die Bearbeitungsgebühr mit oder ohne 19 % MwSt. berechnet wird. Muss vor dem Ausführen gespeichert sein. Bei Reverse-Charge- und Export-Buchungen ist nur ohne MwSt. möglich.",
    "chargeFeeVatModePlaceholder": "Bitte auswählen",
    "chargeFeeWithVat": "Bruttopreis berechnen (Inkl. 19% MwSt.)",
    "chargeFeeWithoutVat": "Nettopreis berechnen (Ohne MwSt.)"
  },
  "es": {
    "infringement": "Infracción administrativa",
    "infringementDetails": "Detalles de la infracción",
    "licencePlate": "Matrícula",
    "documentLanguage": "Idioma del documento",
    "country": "País",
    "authority": "Autoridad",
    "email": "Correo electrónico",
    "date": "Fecha",
    "fileNumber": "Número de expediente",
    "feeLabel": "Tasa",
    "infringementType": "Tipo de infracción",
    "comment": "Comentario",
    "commentPlaceholder": "Añadir comentario interno",
    "checkAndExecute": "Comprobar y ejecutar acciones",
    "awaitingUserConfirmationDescription": "Confirme la infracción y ejecute la acción. Con la confirmación se cobrará automáticamente la tasa de tramitación, se creará una factura y se enviarán los datos del conductor a la autoridad correspondiente.",
    "confirmInfringementAndExecute": "Confirmar infracción y ejecutar acciones",
    "confirmExecuteTitle": "¿Ejecutar infracción?",
    "confirmExecuteDescription": "Se cobrará la tasa de tramitación, se creará una factura y, cuando corresponda, se enviarán los datos del conductor a la autoridad. Esta acción no se puede deshacer.",
    "confirmExecute": "Ejecutar",
    "confirmRetryTitle": "¿Reintentar paso?",
    "confirmRetryDescription": "¿Desea reintentar \"{step}\"?",
    "duplicateFileNumberCannotExecute": "Esta infracción está marcada como duplicado (mismo número de expediente y correo que otro caso). No se permite la ejecución. Corrija los datos si se trata de un error.",
    "foundBookingAndDriverData": "Reserva y datos del conductor encontrados",
    "wunderRentalId": "ID de alquiler Wunder",
    "rosBookingNumber": "Número de reserva ROS",
    "bookingPeriod": "Periodo de reserva",
    "booked": "Reservado",
    "actual": "Real",
    "driverPhone": "Teléfono del conductor",
    "driverEmail": "Correo del conductor",
    "driverData": "Datos del conductor",
    "scan": "Escaneo",
    "openScanPdfPreview": "Abrir vista previa del PDF",
    "infringementImage": "Escaneo de la infracción",
    "couldNotLoadScanPdf": "No se pudo cargar el PDF del escaneo. Inténtelo de nuevo.",
    "retry": "Reintentar",
    "processing": "Procesando",
    "noBookingFoundHint": "Aún no se ha encontrado ninguna reserva. Puede",
    "or": "o",
    "setBookingManually": "establecer la reserva manualmente",
    "viewDuplicateOriginal": "Ver original",
    "deleteInfringement": "Eliminar infracción",
    "confirmDeleteInfringement": "¿Seguro que desea eliminar esta infracción?",
    "countryInfoText": "Se utiliza para determinar la tasa de tramitación según la configuración de yield.",
    "waiveHint": "Si es culpa nuestra o similar, puede que la infracción",
    "waiveButton": "corra por nuestra cuenta",
    "waiveModalTitle": "Eximir infracción",
    "waiveModalDescription": "La infracción se marcará como completada. No se cobrará ninguna tasa ni se enviarán datos del conductor. Seleccione un motivo.",
    "waiveCommentDescription": "Describa el motivo en texto (obligatorio para «Otro»).",
    "waiveConfirm": "Confirmar y completar",
    "waiveReasonRequired": "Seleccione un motivo.",
    "waiveReasonCommentRequired": "Se requiere una justificación por escrito.",
    "waiveReasonCommentTitle": "Detalles",
    "companyAbsorption": "Absorbida a cargo de la empresa.",
    "waiveReason": "Motivo",
    "waiveReasonComment": "Detalles",
    "waiveReasonCommentPlaceholder": "Describa el motivo (obligatorio para «Otro»)",
    "waiveReasons": {
      "LATE_PARKING_VIOLATION": "Infracción de aparcamiento tras 48 horas",
      "INTERNAL": "Interno",
      "OTHER": "Otro"
    },
    "skipSendAuthorityMail": "Correo a la autoridad",
    "skipSendAuthorityMailInfo": "Si los datos del conductor se enviarán por otra vía (p. ej. porque la autoridad no acepta correos), este paso puede omitirse",
    "authorityMailChoiceSend": "Enviar a la autoridad",
    "authorityMailChoiceSkip": "No enviar",
    "chargeFeeVatMode": "IVA de la tasa de tramitación",
    "chargeFeeVatModeInfo": "Elija si la tasa de tramitación se cobra con o sin el 19 % de IVA. Debe guardarse antes de la ejecución. Las reservas con inversión del sujeto pasivo o exportación solo se pueden cobrar sin IVA.",
    "chargeFeeVatModePlaceholder": "Seleccione",
    "chargeFeeWithVat": "Calcular precio bruto (IVA 19 % incluido)",
    "chargeFeeWithoutVat": "Calcular precio neto (sin IVA)"
  }
}
</i18n>
